import { useEffect, useState } from 'react'
import { useHistory, useParams, Prompt } from 'react-router-dom'
import styled from 'styled-components'
import { Moment } from 'moment'

import {
	Badge,
	Button,
	Checkbox,
	Collapse,
	DatePicker,
	Divider,
	Form,
	Input,
	InputNumber,
	Radio,
	Switch,
	Tabs,
	Tooltip,
	notification,
} from 'antd'

import {
	faArrowLeft,
	faBellOn,
	faCloudCheck,
	faCloudPlus,
	faExclamationCircle,
	faMars,
	faPlus,
	faSpinnerThird,
	faSync,
	faTransgender,
	faTrash,
	faVenus,
} from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useIsCreate } from '../../../hooks/is-create'
import { DeleteModal } from '../../shared/delete-modal'
import { MunicipalitySelector } from './municipality-selector'
import { useSaveControls } from '../../../hooks/save-controls'
import { UserSelector } from './user-selctor'

import { txt } from '../../../locales/i18n'
import * as NotificationService from '../../../services/bb-api/notification'

type FormValues = {
	scheduled: Moment
	active: boolean
	content: {
		lang: { [key: string]: { title: string; message: string } }
		municipalityIds: {
			values: number[]
			region: 'Denmark' | 'Sweden' | 'Germany'
			status: 'all' | 'active' | 'inactive'
		}
	}
}

const requiredRule = { required: true, message: txt('global.required') }

export const NotificationPage = () => {
	const history = useHistory()
	const [loading, setLoading] = useState(true)
	const [active, setActive] = useState(false)
	const [valuesChanged, setValuesChanged] = useState(false)
	const [deleteModalOpened, setDeleteModalOpened] = useState(false)
	const [exposureCount, setExposureCount] = useState<number | null>(null)
	const [exposureTab, setExposureTab] = useState<'segments' | 'users'>('segments')
	const [defaultLangPanelsOpen, setDefaultLangPanelsOpen] = useState<string[] | null>(null)

	const { notificationId } = useParams<{ notificationId: string }>()
	const isCreate = useIsCreate(notificationId)

	const [form] = Form.useForm<FormValues>()
	useSaveControls(form.submit)

	useEffect(() => {
		if (isCreate) {
			setDefaultLangPanelsOpen([])
			setExposureCount(0)
			return setLoading(false)
		}

		const openLanguagePanels = (notification: FormValues) => {
			const langs = notification?.content?.lang ?? {}

			const activeLangPanels = Object.entries(langs).reduce<string[]>((activeLangPanels, [key, value]) => {
				if (value.message || value.title) activeLangPanels.push(key)
				return activeLangPanels
			}, [])

			setDefaultLangPanelsOpen(activeLangPanels)
		}

		const getNotification = async () => {
			try {
				const notification = await NotificationService.get(notificationId)
				form.setFieldsValue(notification)

				setActive(notification.active)
				openLanguagePanels(notification)
				if (notification?.content?.users) setExposureTab('users')

				try {
					if ('municipalityIds' in notification.content) {
						const exposureCount = await NotificationService.getExposureCount(notification)
						setExposureCount(exposureCount)
					}

					if ('users' in notification.content) {
						const exposureCount = notification?.content?.users.length ?? 0
						setExposureCount(exposureCount)
					}
				} catch (error) {
					const message = txt('notification.unknown error')
					notification.error({ message })
				}
			} catch (error) {
				const message = txt('global.not found')
				notification.error({ message })
				history.goBack()
			}

			setDefaultLangPanelsOpen([])
			setLoading(false)
		}

		getNotification()
	}, [notificationId, form, history, isCreate])

	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (valuesChanged) {
				event.preventDefault()
				event.returnValue = txt('global.unsaved changes')
			}
		}

		window.addEventListener('beforeunload', handleBeforeUnload)
		return () => window.removeEventListener('beforeunload', handleBeforeUnload)
	}, [valuesChanged])

	useEffect(() => {
		form.setFields([{ name: 'active', value: active }])
	}, [form, active])

	const create = async (formValues: FormValues) => {
		setLoading(true)

		try {
			const notificationId = await NotificationService.create(formValues)
			setValuesChanged(false)

			history.replace('/notifications/' + notificationId)
			const message = txt('notification.create successful')
			notification.success({ message })
		} catch (error) {
			const message = txt('notification.create failed')
			notification.error({ message })
		}

		setLoading(false)
	}

	const update = async (formValues: FormValues) => {
		setLoading(true)

		try {
			await NotificationService.update(notificationId, formValues)
			setValuesChanged(false)

			const message = txt('notification.create successful')
			notification.success({ message })
		} catch (error) {
			const message = txt('notification.create failed')
			notification.error({ message })
		}

		setLoading(false)
	}

	const previewNotification = async () => {
		const message = txt('global.sending push notification')
		notification.info({ message })

		try {
			await NotificationService.preview(notificationId)

			const message = txt('notification.create successful')
			notification.success({ message })
		} catch (error) {
			const message = txt('notification.create failed')
			notification.error({ message })
		}
	}

	const getExposureCount = async () => {
		setExposureCount(null)
		const values = form.getFieldsValue()

		try {
			const exposureCount = await NotificationService.getExposureCount(values)
			setExposureCount(exposureCount)
		} catch (error) {
			const message = txt('notification.unknown error')
			notification.error({ message })
		}
	}

	const exposureTabHandler = (tab: string) => {
		form.setFields([
			{ name: ['content', 'users'], value: [] },
			{ name: ['content', 'municipalityIds', 'values'], value: [] },
			{ name: ['content', 'municipalityIds', 'status'], value: 'all' },
		])

		setValuesChanged(true)
		setExposureTab(tab as 'segments' | 'users')
		setExposureCount(0)
	}

	const renderExposureTab = (target: typeof exposureTab) => (
		<>
			{txt('global.' + target)}{' '}
			{(target === exposureTab && exposureCount) ?? <FontAwesomeIcon icon={faSpinnerThird} spin />}
		</>
	)

	const formChangeHandler = async (values: any) => {
		setValuesChanged(true)

		if (values?.content?.users) {
			const exposureCount = values?.content?.users.length ?? 0
			setExposureCount(exposureCount)
		}

		const exposureKeys = ['genders', 'subscriber', 'municipalityIds', 'ageSegments']
		const [changedKey = null] = Object.keys(values?.content ?? {})

		if (changedKey && exposureKeys.includes(changedKey)) {
			await getExposureCount()
		}
	}

	return (
		<MainStyled>
			<Prompt when={valuesChanged} message={txt('global.unsaved changes')} />

			<div className="actions-container horizontal gap aligned-center">
				{isCreate && (
					<Button type="link" loading={loading} onClick={() => history.goBack()}>
						<FontAwesomeIcon icon={faArrowLeft} />
					</Button>
				)}

				<Tooltip title={txt('global.active')}>
					<Switch size="small" loading={loading} className="active-switch" checked={active} onChange={setActive} />
				</Tooltip>

				<Tooltip title={isCreate ? txt('global.create') : txt('global.update')}>
					<Badge
						size="small"
						offset={[-15, 10]}
						count={
							valuesChanged && isCreate === false ? <FontAwesomeIcon color="orange" icon={faExclamationCircle} /> : null
						}
					>
						<Button type="link" loading={loading} onClick={form.submit}>
							<FontAwesomeIcon icon={isCreate ? faCloudPlus : faCloudCheck} />
						</Button>
					</Badge>
				</Tooltip>

				{isCreate === false && (
					<>
						<Tooltip title={txt('global.test')}>
							<Button type="link" loading={loading} onClick={previewNotification}>
								<FontAwesomeIcon icon={faBellOn} />
							</Button>
						</Tooltip>

						<Tooltip title={txt('global.delete')}>
							<Button type="link" loading={loading} danger onClick={() => setDeleteModalOpened(true)}>
								<FontAwesomeIcon icon={faTrash} />
							</Button>
						</Tooltip>
					</>
				)}
			</div>

			<Form form={form} onFinish={isCreate ? create : update} onValuesChange={formChangeHandler}>
				<Form.Item name="active" initialValue={false} valuePropName="checked" className="hide">
					<Switch />
				</Form.Item>

				<section className="form-section">
					<h1>{txt('global.notification')}</h1>

					<div className="card vertical">
						<div className="horizontal gap justified-center">
							<div className="vertical">
								<label>{txt('global.name')}</label>

								<Form.Item name="name" rules={[requiredRule]}>
									<Input placeholder={txt('global.name')} allowClear />
								</Form.Item>
							</div>

							<div className="vertical">
								<label>{txt('global.schedule')}</label>

								<Form.Item name="scheduled" rules={[requiredRule]}>
									<DatePicker showTime showSecond={false} minuteStep={15} format="dddd HH:mm DD/MM-YY" />
								</Form.Item>
							</div>
							{/* 
							<div className="vertical">
								<label>{txt('global.show in app')}</label>

								<span>
									<Form.Item name={['content', 'prompt']} valuePropName="checked">
										<Switch />
									</Form.Item>
								</span>
							</div> */}
						</div>

						<Divider />

						<b className="language">{txt('languages.danish')}</b>

						<label>{txt('global.title')}</label>
						<Form.Item name={['content', 'lang', 'da', 'title']} rules={[requiredRule]}>
							<Input allowClear placeholder="Aa" />
						</Form.Item>

						<label>{txt('global.message')}</label>
						<Form.Item name={['content', 'lang', 'da', 'message']} rules={[requiredRule]}>
							<Input.TextArea allowClear placeholder="Aa" />
						</Form.Item>

						<b className="language">{txt('languages.english')}</b>

						<label>{txt('global.title')}</label>
						<Form.Item name={['content', 'lang', 'en', 'title']} rules={[requiredRule]}>
							<Input allowClear placeholder="Aa" />
						</Form.Item>

						<label>{txt('global.message')}</label>
						<Form.Item name={['content', 'lang', 'en', 'message']} rules={[requiredRule]}>
							<Input.TextArea allowClear placeholder="Aa" />
						</Form.Item>

						{defaultLangPanelsOpen !== null && (
							<Collapse ghost defaultActiveKey={defaultLangPanelsOpen}>
								<Collapse.Panel forceRender header={<b className="language">{txt('languages.swedish')}</b>} key="sv">
									<label>{txt('global.title')}</label>
									<Form.Item name={['content', 'lang', 'sv', 'title']}>
										<Input allowClear placeholder="Aa" />
									</Form.Item>

									<label>{txt('global.message')}</label>
									<Form.Item name={['content', 'lang', 'sv', 'message']}>
										<Input.TextArea allowClear placeholder="Aa" />
									</Form.Item>
								</Collapse.Panel>

								<Collapse.Panel forceRender header={<b className="language">{txt('languages.deutsch')}</b>} key="de">
									<label>{txt('global.title')}</label>
									<Form.Item name={['content', 'lang', 'de', 'title']}>
										<Input allowClear placeholder="Aa" />
									</Form.Item>

									<label>{txt('global.message')}</label>
									<Form.Item name={['content', 'lang', 'de', 'message']}>
										<Input.TextArea allowClear placeholder="Aa" />
									</Form.Item>
								</Collapse.Panel>

								<Collapse.Panel forceRender header={<b className="language">{txt('languages.finnish')}</b>} key="fi">
									<label>{txt('global.title')}</label>
									<Form.Item name={['content', 'lang', 'fi', 'title']}>
										<Input allowClear placeholder="Aa" />
									</Form.Item>

									<label>{txt('global.message')}</label>
									<Form.Item name={['content', 'lang', 'fi', 'message']}>
										<Input.TextArea allowClear placeholder="Aa" />
									</Form.Item>
								</Collapse.Panel>

								<Collapse.Panel forceRender header={<b className="language">{txt('languages.ukrainian')}</b>} key="uk">
									<label>{txt('global.title')}</label>
									<Form.Item name={['content', 'lang', 'uk', 'title']}>
										<Input allowClear placeholder="Aa" />
									</Form.Item>

									<label>{txt('global.message')}</label>
									<Form.Item name={['content', 'lang', 'uk', 'message']}>
										<Input.TextArea allowClear placeholder="Aa" />
									</Form.Item>
								</Collapse.Panel>

								<Collapse.Panel forceRender header={<b className="language">{txt('languages.arabic')}</b>} key="ar">
									<label>{txt('global.title')}</label>
									<Form.Item name={['content', 'lang', 'ar', 'title']}>
										<Input allowClear placeholder="Aa" />
									</Form.Item>

									<label>{txt('global.message')}</label>
									<Form.Item name={['content', 'lang', 'ar', 'message']}>
										<Input.TextArea allowClear placeholder="Aa" />
									</Form.Item>
								</Collapse.Panel>
							</Collapse>
						)}
					</div>
				</section>

				<section className="form-section">
					<h1>{txt('global.exposure')}</h1>

					<div className="card vertical">
						<Tabs
							activeKey={exposureTab}
							destroyInactiveTabPane
							onChange={exposureTabHandler}
							tabBarExtraContent={
								exposureTab === 'segments' ? (
									<Tooltip title={txt('global.update')}>
										<Button type="link" icon={<FontAwesomeIcon icon={faSync} />} onClick={getExposureCount} />
									</Tooltip>
								) : null
							}
						>
							<Tabs.TabPane tab={renderExposureTab('segments')} key="segments">
								<MunicipalitySelector
									label={txt('global.visibility')}
									name={['content', 'municipalityIds']}
									form={form}
									onChange={async () => {
										setValuesChanged(true)
										setTimeout(getExposureCount, 100)
									}}
								/>

								<Divider />

								<div className="horizontal gap wrap justified-center">
									<div className="vertical">
										<label>{txt('global.gender')}</label>

										<Form.Item name={['content', 'genders']} initialValue={[1, 2, 3]}>
											<Checkbox.Group>
												<Checkbox value={1}>
													<span>{txt('genders.male')} </span>
													<FontAwesomeIcon icon={faMars} />
												</Checkbox>

												<Checkbox value={2}>
													<span>{txt('genders.female')} </span>
													<FontAwesomeIcon icon={faVenus} />
												</Checkbox>

												<Checkbox value={3}>
													<span>{txt('genders.other')} </span>
													<FontAwesomeIcon icon={faTransgender} />
												</Checkbox>
											</Checkbox.Group>
										</Form.Item>
									</div>

									<div className="vertical">
										<label>{txt('global.subscription')}</label>

										<Form.Item name={['content', 'subscriber']} initialValue="inactive">
											<Radio.Group>
												<Radio value="all">{txt('global.all')}</Radio>
												<Radio value="active">{txt('global.active')}</Radio>
												<Radio value="inactive">{txt('global.inactive')}</Radio>
											</Radio.Group>
										</Form.Item>
									</div>
								</div>

								<div>
									<label>{txt('global.age')}</label>

									<div className="vertical aligned-center">
										<Form.List name={['content', 'ageSegments']}>
											{(fields, { add, remove }) => (
												<>
													{fields.map(({ key, name, ...restField }) => (
														<div key={key} className="horizontal gap aligned-center">
															<div>
																<label>{txt('global.from')}</label>
																<Form.Item {...restField} name={[name, 'from']} initialValue={0} rules={[requiredRule]}>
																	<InputNumber min={0} />
																</Form.Item>
															</div>

															<div>
																<label>{txt('global.to')}</label>
																<Form.Item {...restField} name={[name, 'to']} initialValue={0} rules={[requiredRule]}>
																	<InputNumber min={0} />
																</Form.Item>
															</div>

															<Button
																danger
																type="text"
																onClick={() => remove(name)}
																icon={<FontAwesomeIcon icon={faTrash} />}
															/>
														</div>
													))}

													<Button type="dashed" block onClick={() => add()}>
														<FontAwesomeIcon icon={faPlus} />
													</Button>
												</>
											)}
										</Form.List>
									</div>
								</div>
							</Tabs.TabPane>

							<Tabs.TabPane key="users" tab={renderExposureTab('users')}>
								<UserSelector form={form} />
							</Tabs.TabPane>
						</Tabs>
					</div>
				</section>
			</Form>

			<DeleteModal
				useVisible={[deleteModalOpened, setDeleteModalOpened]}
				onConfirm={async () => {
					await NotificationService.remove(notificationId)
					history.goBack()
				}}
			/>
		</MainStyled>
	)
}

const MainStyled = styled.main`
	position: relative;
	background-color: ghostwhite;

	display: flex;
	flex-direction: column;
	align-items: center;

	h1 {
		color: #89a;
	}

	label {
		color: #789;
	}

	.language {
		color: #678;
		font-size: 1rem;
	}

	form {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 1rem;

		width: 100%;
		padding: 1rem;
	}

	.actions-container {
		box-shadow: 0 0 1rem ghostwhite;
		position: sticky;
		top: 0;
		z-index: 1;

		background-color: white;
		padding: 0.2rem 1rem;
		width: 100%;

		.active-switch {
			margin-right: auto;
		}
	}

	.card {
		border-radius: 1rem;
		box-shadow: 0 0 1rem #89a3;

		background-color: white;
		padding: 1rem;
	}

	.form-section {
		width: 100%;
		max-width: 640px;
	}

	.flag {
		width: 2rem;
	}

	.hide {
		display: none;
	}

	.ant-collapse .ant-collapse-item .ant-collapse-content-box {
		padding-block: 0;
	}
`
